/**
 * Error detection utilities for Tauri IPC events.
 * These are pure functions that can be easily unit tested.
 */

export type ErrorPayload = { message: string };

// Error message patterns for device-related errors
const DEVICE_UNPLUGGED_MESSAGE =
  'The requested device is no longer available. For example, it has been unplugged.';
const DEVICE_NO_LONGER_AVAILABLE = 'device is no longer available';
const COULD_NOT_FIND_INPUT_DEVICE = 'could not find a suitable input device';
const COULD_NOT_FIND_OUTPUT_DEVICE = 'could not find a suitable output device';

// Error message patterns for permission errors
const PERMISSION_DENIED = 'permission denied';
const ACCESS_DENIED = 'access denied';

// Error message patterns for empty stream errors
const MICROPHONE_EMPTY_ERROR = '[Recording Error]: Microphone is empty';
const SPEAKER_EMPTY_ERROR = '[Recording Error]: Speaker is empty';

/**
 * Checks if the error payload indicates an audio device was lost/disconnected.
 * Handles various error message formats from different platforms.
 */
export function isDeviceLostError(payload: ErrorPayload): boolean {
  const payloadString = JSON.stringify(payload);
  const payloadLower = payloadString.toLowerCase();

  return (
    payloadString.includes(DEVICE_UNPLUGGED_MESSAGE) ||
    payloadLower.includes(DEVICE_NO_LONGER_AVAILABLE) ||
    payloadLower.includes(COULD_NOT_FIND_INPUT_DEVICE) ||
    payloadLower.includes(COULD_NOT_FIND_OUTPUT_DEVICE)
  );
}

/**
 * Checks if the error payload indicates a permission was denied.
 */
export function isPermissionDeniedError(payload: ErrorPayload): boolean {
  const payloadLower = JSON.stringify(payload).toLowerCase();
  return payloadLower.includes(PERMISSION_DENIED) || payloadLower.includes(ACCESS_DENIED);
}

/**
 * Checks if the error payload indicates the microphone stream is empty.
 */
export function isMicrophoneEmptyError(payload: ErrorPayload): boolean {
  return JSON.stringify(payload).includes(MICROPHONE_EMPTY_ERROR);
}

/**
 * Checks if the error payload indicates the speaker stream is empty.
 */
export function isSpeakerEmptyError(payload: ErrorPayload): boolean {
  return JSON.stringify(payload).includes(SPEAKER_EMPTY_ERROR);
}
